// ─────────────────────────────────────────────────────────────────────────────
// RaceSplitsCard.js — post-race 500m split summary.
//
// Two render contexts:
//   1) Inline modal in WorkoutMonitorV2 the moment a race finishes
//   2) Embedded card inside WorkoutHistory expanded view
//
// Displays:
//   • 500m split bars relative to the row's average pace
//     (faster than avg = green, slower = rose)
//   • Numeric split column (m:ss for that 500m)
//   • Cumulative time at each boundary
//   • Avg split / fastest split / spread (consistency)
//   • Optional `priorBest` prop renders a dashed pace line for PR comparison
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { Trophy, X, Anchor, MapPin } from 'lucide-react';

function fmtSplit(ms) {
  if (typeof ms !== 'number' || ms <= 0) return '—';
  const total = ms / 1000;
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}

function fmtTime(ms) {
  if (typeof ms !== 'number' || ms < 0) return '—';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function deriveStats(splits) {
  if (!Array.isArray(splits) || splits.length === 0) return null;
  const splitMs = splits.map(s => s.split_ms).filter(v => typeof v === 'number' && v > 0);
  if (splitMs.length === 0) return null;
  const avg = splitMs.reduce((a, b) => a + b, 0) / splitMs.length;
  const fastest = Math.min(...splitMs);
  const slowest = Math.max(...splitMs);
  const spread = slowest - fastest;
  return { avg, fastest, slowest, spread };
}

function SplitRow({ split, prevElapsed, avgMs, isFastest }) {
  // Relative bar: 0% center = at avg pace, scale ±20% from avg
  const deviation = (split.split_ms - avgMs) / avgMs;          // negative = faster
  const widthPct = Math.min(100, Math.abs(deviation) * 100 / 0.2 * 50); // map ±20% → 0..50% half-bar
  const isFaster = deviation < 0;
  const barColor = isFaster ? 'bg-emerald-500' : 'bg-rose-500';
  const labelColor = isFaster ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
  const deltaSec = Math.abs(deviation * avgMs) / 1000;

  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`race-split-row-${split.boundary_m}`}>
      <div className="col-span-2 text-xs font-mono font-bold text-zinc-700 dark:text-zinc-200">
        {split.boundary_m}m
      </div>
      <div className="col-span-3 text-sm font-mono font-bold text-zinc-900 dark:text-white flex items-center gap-1">
        {fmtSplit(split.split_ms)}
        {isFastest && <Trophy className="w-3 h-3 text-amber-500" />}
      </div>
      {/* Bar centered around the avg line */}
      <div className="col-span-5 relative h-4 bg-zinc-100 dark:bg-zinc-700 rounded overflow-hidden">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-zinc-400 dark:bg-zinc-500" />
        <div
          className={`absolute top-0 bottom-0 ${barColor}`}
          style={{
            [isFaster ? 'right' : 'left']: '50%',
            width: `${widthPct}%`,
          }}
        />
      </div>
      <div className={`col-span-1 text-[11px] font-mono ${labelColor} text-right`}>
        {isFaster ? '−' : '+'}{deltaSec.toFixed(1)}
      </div>
      <div className="col-span-1 text-[11px] font-mono text-zinc-500 dark:text-zinc-400 text-right">
        {fmtTime(split.elapsed_ms)}
      </div>
    </div>
  );
}

export default function RaceSplitsCard({
  splits = [],
  raceDistance,
  onClose,
  variant = 'inline',         // 'modal' | 'inline'
  priorBest,                  // optional: { total_ms, splits? } for PR comparison
  autoDismissMs,              // optional: auto-close after this many ms (modal only)
  context,                    // optional: { date, isErgo, boatClass, savedToHistory }
}) {
  const stats = useMemo(() => deriveStats(splits), [splits]);
  const [remainingMs, setRemainingMs] = useState(autoDismissMs || 0);

  // Auto-dismiss countdown (modal variant)
  useEffect(() => {
    if (variant !== 'modal' || !autoDismissMs || !onClose) return undefined;
    const startedAt = Date.now();
    setRemainingMs(autoDismissMs);
    const tick = setInterval(() => {
      const left = autoDismissMs - (Date.now() - startedAt);
      if (left <= 0) {
        clearInterval(tick);
        try { onClose(); } catch (_e) { /* noop */ }
      } else {
        setRemainingMs(left);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [variant, autoDismissMs, onClose]);

  if (!Array.isArray(splits) || splits.length === 0) return null;
  const totalMs = splits[splits.length - 1].elapsed_ms;
  const distLabel = raceDistance >= 1000 ? `${raceDistance / 1000}K` : `${raceDistance}m`;
  const contextDate = context?.date ? new Date(context.date) : null;
  const dateLabel = contextDate
    ? contextDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const ergoLabel = typeof context?.isErgo === 'boolean'
    ? (context.isErgo ? 'Ergo' : 'On water')
    : null;
  const boatLabel = context?.boatClass
    ? String(context.boatClass).toUpperCase()
    : null;
  const autoDismissSec = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : null;
  const autoDismissDisplay = autoDismissSec
    ? `${Math.floor(autoDismissSec / 60)}:${String(autoDismissSec % 60).padStart(2, '0')}`
    : null;

  const card = (
    <div
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm overflow-hidden"
      data-testid="race-splits-card"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {distLabel} Race Splits
            </h3>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
              Total <span className="font-mono font-bold text-zinc-700 dark:text-zinc-200">{fmtTime(totalMs)}</span>
              {priorBest?.total_ms && (
                <>
                  {' · '}
                  PR <span className="font-mono font-bold">{fmtTime(priorBest.total_ms)}</span>
                  {' '}
                  <span className={totalMs <= priorBest.total_ms ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-rose-600 dark:text-rose-400 font-bold'}>
                    ({totalMs <= priorBest.total_ms ? '−' : '+'}{Math.abs((totalMs - priorBest.total_ms) / 1000).toFixed(1)}s)
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        {variant === 'modal' && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
            data-testid="race-splits-close"
            aria-label="Close splits card"
          >
            <X className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
          </button>
        )}
      </div>

      {/* Context strip — parent session info (date, water/ergo, boat class) */}
      {(dateLabel || ergoLabel || boatLabel) && (
        <div
          className="px-4 py-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-600 dark:text-zinc-300 border-b border-zinc-100 dark:border-zinc-700 bg-zinc-50/40 dark:bg-zinc-900/20"
          data-testid="race-splits-context"
        >
          {dateLabel && (
            <span className="inline-flex items-center gap-1 font-mono">
              <MapPin className="w-3 h-3 text-zinc-400" />
              {dateLabel}
            </span>
          )}
          {ergoLabel && (
            <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[10px] bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200">
              {ergoLabel}
            </span>
          )}
          {boatLabel && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              <Anchor className="w-3 h-3" />
              {boatLabel}
            </span>
          )}
          {context?.savedToHistory && (
            <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[10px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
              Saved to History
            </span>
          )}
          {autoDismissDisplay && (
            <span className="ml-auto font-mono text-zinc-500 dark:text-zinc-400" data-testid="race-splits-autodismiss">
              Closes in {autoDismissDisplay}
            </span>
          )}
        </div>
      )}

      {/* Stats strip */}
      {stats && (
        <div className="px-4 py-3 grid grid-cols-3 gap-3 border-b border-zinc-100 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-900/30">
          <div data-testid="race-splits-stat-avg">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Avg 500m</div>
            <div className="text-base font-mono font-bold text-zinc-900 dark:text-white">{fmtSplit(stats.avg)}</div>
          </div>
          <div data-testid="race-splits-stat-fastest">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Fastest 500m</div>
            <div className="text-base font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmtSplit(stats.fastest)}</div>
          </div>
          <div data-testid="race-splits-stat-spread">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Consistency</div>
            <div className="text-base font-mono font-bold text-zinc-900 dark:text-white">±{(stats.spread / 1000 / 2).toFixed(1)}s</div>
          </div>
        </div>
      )}

      {/* Splits header row */}
      <div className="px-4 pt-3 pb-2 grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        <div className="col-span-2">Mark</div>
        <div className="col-span-3">Split</div>
        <div className="col-span-5 text-center">vs Avg</div>
        <div className="col-span-1 text-right">Δ</div>
        <div className="col-span-1 text-right">Total</div>
      </div>

      {/* Splits rows */}
      <div className="px-4 pb-4 space-y-2" data-testid="race-splits-rows">
        {splits.map((s) => (
          <SplitRow
            key={s.boundary_m}
            split={s}
            prevElapsed={0}
            avgMs={stats?.avg || s.split_ms}
            isFastest={stats && s.split_ms === stats.fastest}
          />
        ))}
      </div>
    </div>
  );

  if (variant === 'modal') {
    return (
      <div
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="race-splits-modal"
      >
        <div
          className="w-full max-w-xl my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {card}
        </div>
      </div>
    );
  }
  return card;
}
